/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AnnouncementsFormView.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Form view for creating and editing Announcements.
    Manages announcement configuration with General and Advanced Settings sections.

 🔧 Notes:
    - Domain-scoped data
    - Supports JSONB settings for advanced FreeSWITCH features
    - Single page with two sections: General and Settings

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Copy, Plus, Search, Volume2, X } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { FormBox } from '../../components/ui/FormBox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import RecordingSelect from '../../components/ui/RecordingSelect';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import Toggle from '../../components/ui/Toggle';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { AnnouncementSettingsSection } from './components/AnnouncementSettingsSection';
import { deleteAnnouncementById, navigateToAnnouncementDuplicate } from './announcementsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { fetchAnnouncement, createAnnouncement, updateAnnouncement, fetchAnnouncements } from './api/announcementApi';
import { Announcement } from './types';

interface AnnouncementSettings {
  allow_terminators: boolean;
  terminator_key: string;
  post_delay: number;
  repeat_times: number;
  transfer_timeout: number | null;
  transfer_behavior: 'immediate' | 'delayed' | 'conditional';
  mode: 'once' | 'loop' | 'background';
  background_music: string;
  beep_before_playback: boolean;
  record_call: boolean;
  failover_module: string;
  failover_data: string;
  event_webhook: string;
  time_condition: { days: string[]; start: string; end: string } | null;
  after_hours_destination: { module: string; data: string } | null;
  max_duration: number | null;
}

interface AnnouncementFormData {
  name: string;
  context: string;
  recordingId: string | null;
  destinationModule: string;
  destinationData: string;
  enabled: boolean;
  settings: AnnouncementSettings;
}

const DEFAULT_SETTINGS: AnnouncementSettings = {
  allow_terminators: false,
  terminator_key: '#',
  post_delay: 0,
  repeat_times: 1,
  transfer_timeout: null,
  transfer_behavior: 'immediate',
  mode: 'once',
  background_music: '',
  beep_before_playback: false,
  record_call: false,
  failover_module: '',
  failover_data: '',
  event_webhook: '',
  time_condition: null,
  after_hours_destination: null,
  max_duration: null,
};

const DEFAULT_FORM: AnnouncementFormData = {
  name: '',
  context: '',
  recordingId: null,
  destinationModule: '',
  destinationData: '',
  enabled: true,
  settings: DEFAULT_SETTINGS,
};

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

const allowedTransferBehaviors = new Set(['immediate', 'delayed', 'conditional']);
const allowedModes = new Set(['once', 'loop', 'background']);

const generateContextFromName = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 80)
    .replace(/^ann_/, '')
    .replace(/^/, 'ann_');

export default function AnnouncementsFormView() {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState<AnnouncementFormData>(deepClone(DEFAULT_FORM));
  const [originalData, setOriginalData] = useState<AnnouncementFormData>(deepClone(DEFAULT_FORM));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<Array<{ id: string; name: string; description?: string | null }>>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const lastSaveTimestamp = useRef(0);
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const quickListLoadingRef = useRef(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Stable refs for functions to prevent re-creations
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData(deepClone(DEFAULT_FORM));
      setOriginalData(deepClone(DEFAULT_FORM));
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('announcements.editEntry')} - ${formData.name || ''}` : t('announcements.createEntry')),
    [isEditMode, t, formData.name],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeAnnouncementResponse = (payload: any): AnnouncementFormData => ({
    name: payload.name || '',
    context: payload.context || '',
    recordingId:
      payload.recordingId != null ? String(payload.recordingId)
      : payload.recording_id != null ? String(payload.recording_id)
      : null,
    destinationModule:
      payload.destinationModule ?? payload.destination_module ?? '',
    destinationData:
      payload.destinationData ?? payload.destination_data ?? '',
    enabled: Boolean(payload.enabled ?? true),
    settings: {
      ...DEFAULT_SETTINGS,
      ...(payload.settings ?? {}),
    },
  });

  const applyLoadedData = useCallback(
    (announcement: Partial<AnnouncementFormData>, { duplicate }: { duplicate: boolean }) => {
      const merged = deepClone({
        ...DEFAULT_FORM,
        ...announcement,
        settings: {
          ...DEFAULT_SETTINGS,
          ...(announcement.settings ?? {}),
        },
      });

      if (!merged.context && merged.name) {
        merged.context = generateContextFromName(merged.name);
      }

      if (duplicate) {
        const copySuffix = tRef.current('common:copySuffix');
        merged.name = `${merged.name} ${copySuffix}`.trim();
        merged.context = generateContextFromName(merged.name);
        // Batch state updates to prevent re-renders during render cycle
        setTimeout(() => {
          setOriginalData(deepClone(DEFAULT_FORM));
          setFormData(deepClone(merged));
        }, 0);
      } else {
        // Batch state updates to prevent re-renders during render cycle
        setTimeout(() => {
          setOriginalData(deepClone(merged));
          setFormData(deepClone(merged));
        }, 0);
      }
    },
    [],
  );

  const applyLoadedDataRef = useRef(applyLoadedData);
  useEffect(() => {
    applyLoadedDataRef.current = applyLoadedData;
  }, [applyLoadedData]);

  const loadAnnouncementFn = useCallback(
    async (announcementId: string, { duplicate }: { duplicate: boolean }) => {
      // Prevent duplicate loads
      if (isLoadingRef.current || lastLoadedIdRef.current === announcementId) {
        return;
      }

      isLoadingRef.current = true;
      lastLoadedIdRef.current = announcementId;
      setIsLoading(true);

      try {
        const announcement = await fetchAnnouncement(announcementId);
        const normalized = normalizeAnnouncementResponse(announcement);
        applyLoadedDataRef.current(normalized, { duplicate });
      } catch (error) {
        lastLoadedIdRef.current = null; // Allow retry on error
        showErrorRef.current(tRef.current('announcements.messages.loadError'));
        navigateRef.current('/pbx/incoming-tools/announcements');
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [],
  );

  const loadAnnouncementRef = useRef(loadAnnouncementFn);
  useEffect(() => {
    loadAnnouncementRef.current = loadAnnouncementFn;
  }, [loadAnnouncementFn]);

  // Load initial data only once
  useEffect(() => {
    if (hasLoadedInitialDataRef.current) return;
    hasLoadedInitialDataRef.current = true;
  }, []);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  // Main effect for loading data when id/duplicateId changes
  useEffect(() => {
    const loadId = duplicateId || id;
    const idChanged = currentIdRef.current !== loadId;

    if (idChanged) {
      currentIdRef.current = loadId;
    }

    if (isEditMode && id && idChanged) {
      setTimeout(() => {
        loadAnnouncementRef.current(id, { duplicate: false });
      }, 0);
    } else if (duplicateId && idChanged) {
      setTimeout(() => {
        loadAnnouncementRef.current(duplicateId, { duplicate: true });
      }, 0);
    } else if (!isEditMode && !duplicateId) {
      setTimeout(() => {
        lastLoadedIdRef.current = null;
      }, 0);
    }
  }, [duplicateId, id, isEditMode]);

  useEffect(() => {
    if (!isEditMode && !duplicateId && formData.name.trim().length > 0) {
      setFormData((prev) => ({
        ...prev,
        context: generateContextFromName(prev.name),
      }));
    }
  }, [duplicateId, formData.name, isEditMode]);

  const handleFieldChange = <K extends keyof AnnouncementFormData>(
    field: K,
    value: AnnouncementFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const handleSettingChange = (field: keyof AnnouncementSettings, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value,
      },
    }));
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = t('announcements.validation.nameRequired');
    }

    if (!formData.recordingId) {
      nextErrors.recordingId = t('announcements.validation.recordingRequired');
    }

    if (!formData.destinationModule.trim()) {
      nextErrors.destinationModule = t('announcements.validation.destinationRequired');
    }

    if (!formData.destinationData.trim()) {
      nextErrors.destinationData = t('announcements.validation.destinationDataRequired');
    }

    if (!allowedTransferBehaviors.has(formData.settings.transfer_behavior)) {
      nextErrors.transferBehavior = t('common.validationError');
    }

    if (!allowedModes.has(formData.settings.mode)) {
      nextErrors.mode = t('common.validationError');
    }

    if (formData.settings.repeat_times < 1 || formData.settings.repeat_times > 5) {
      nextErrors.repeat_times = t('announcements.validation.repeatTimes');
    }

    if (formData.settings.post_delay < 0) {
      nextErrors.post_delay = t('announcements.validation.postDelay');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent): Promise<Announcement | undefined> => {
      event?.preventDefault();

      // Double-save prevention check before setIsSaving
      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return undefined;
      }
      lastSaveTimestamp.current = now;

      if (!validateForm()) {
        showErrorRef.current(tRef.current('announcements.messages.validationFailed', { defaultValue: tRef.current('common.validationError') }));
        return undefined;
      }

      setIsSaving(true);
      try {
        const payload = {
          name: formData.name.trim(),
          context: formData.context.trim() || generateContextFromName(formData.name),
          recordingId: formData.recordingId,
          destinationModule: formData.destinationModule.trim(),
          destinationData: formData.destinationData.trim(),
          enabled: formData.enabled,
          settings: {
            ...formData.settings,
            repeat_times: Math.min(Math.max(formData.settings.repeat_times, 1), 5),
            post_delay: Math.max(formData.settings.post_delay, 0),
          },
        };

        const result = isEditMode
          ? await updateAnnouncement(id!, payload)
          : await createAnnouncement(payload);

        showSuccessRef.current(
          isEditMode
            ? tRef.current('announcements.messages.updateSuccess')
            : tRef.current('announcements.messages.createSuccess'),
        );

        setOriginalData(deepClone(formData));

        // Return saved data for useSaveWithAction
        return result;
      } catch (error) {
        showErrorRef.current(error.message || tRef.current('announcements.messages.createError'));
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, id, isEditMode, validateForm],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: String(id), uuid: String(id) } as unknown as Announcement;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/announcements',
    newPath: '/pbx/incoming-tools/announcements/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Announcements use ID for navigation
      return `/pbx/incoming-tools/announcements/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'announcements:saveAndNewData'
  });

  const handleCancel = () => {
    if (isEditMode) {
      setFormData(deepClone(originalData));
      setErrors({});
    } else {
      navigateRef.current('/pbx/incoming-tools/announcements');
    }
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteAnnouncementById(id);
      showSuccessRef.current(tRef.current('announcements.messages.deleteSuccess'));
      navigateRef.current('/pbx/incoming-tools/announcements');
    } catch (error) {
      showErrorRef.current(error.message || tRef.current('announcements.messages.deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, isEditMode]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToAnnouncementDuplicate(navigateRef.current, id);
  }, [id, isEditMode]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const result = await fetchAnnouncements(
        { page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' },
      );
      const items = Array.isArray(result?.data)
        ? result.data.map((a) => ({ id: a.id, name: a.name, description: null }))
        : [];
      setQuickListItems(items);
    } catch {
      showErrorRef.current(tRef.current('announcements.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  if (isLoading) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Volume2 className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('announcements.actions.list')}
        onBack={() => navigateRef.current('/pbx/incoming-tools/announcements')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('announcements.quickList.tooltip', 'Search'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode && !duplicateId
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('announcements.actions.add'),
            onClick: () => navigateRef.current('/pbx/incoming-tools/announcements/new'),
          },
        ]}
      />

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <FormBox
          title={t('announcements.sections.general')}
          icon={<Volume2 className="h-5 w-5" />}
        >
          <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Label htmlFor="name">
              <SimpleTooltip content={t('announcements.fields.name.tooltip')}>
                <span>
                  {t('announcements.fields.name.label')}
                  <span className="ml-1 text-red-500">*</span>
                </span>
              </SimpleTooltip>
            </Label>
            <div>
              <Input
                id="name"
                value={formData.name}
                onChange={(event) => {
                  const nextName = event.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    name: nextName,
                    context: generateContextFromName(nextName),
                  }));
                }}
                placeholder={t('announcements.fields.name.placeholder')}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <Label htmlFor="recording">
              <SimpleTooltip content={t('announcements.fields.recording.tooltip')}>
                <span>
                  {t('announcements.fields.recording.label')}
                  <span className="ml-1 text-red-500">*</span>
                </span>
              </SimpleTooltip>
            </Label>
            <div>
              <RecordingSelect
                value={formData.recordingId ?? ''}
                onChange={(value) => {
                  handleFieldChange('recordingId', value || null);
                }}
                placeholder={t('announcements.fields.recording.placeholder')}
                returnType="id"
                showPlayButton
                showTtsButton
                hasError={Boolean(errors.recordingId)}
              />
              {errors.recordingId && (
                <p className="mt-1 text-sm text-red-500">{errors.recordingId}</p>
              )}
            </div>

            <Label>
              <SimpleTooltip content={t('announcements.fields.destination.tooltip')}>
                <span>
                  {t('announcements.fields.destination.label')}
                  <span className="ml-1 text-red-500">*</span>
                </span>
              </SimpleTooltip>
            </Label>
            <div className="col-span-3">
              <ModuleDestinationSelect
                moduleValue={formData.destinationModule}
                destinationValue={formData.destinationData}
                onModuleChange={(value) => handleFieldChange('destinationModule', value)}
                onDestinationChange={(value) => handleFieldChange('destinationData', value)}
                modulePlaceholder={t('announcements.fields.destination.modulePlaceholder')}
                destinationPlaceholder={t('announcements.fields.destination.destinationPlaceholder')}
              />
              {(errors.destinationModule || errors.destinationData) && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.destinationModule || errors.destinationData}
                </p>
              )}
            </div>

            <Label htmlFor="enabled">
              <SimpleTooltip content={t('announcements.fields.enabled.tooltip')}>
                {t('announcements.fields.enabled.label')}
              </SimpleTooltip>
            </Label>
            <div>
              <Toggle
                value={formData.enabled}
                onChange={(value) => handleFieldChange('enabled', value)}
              />
            </div>
          </div>
        </FormBox>

        <AnnouncementSettingsSection
          settings={formData.settings}
          errors={errors}
          onSettingChange={handleSettingChange}
          t={t}
        />

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={handleCancel}
          onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
          isEditMode={isEditMode}
          isSaving={isSaving}
          isLoading={isLoading}
          saveText={t('announcements.actions.save')}
          cancelText={t('announcements.actions.cancel')}
          deleteText={t('announcements.actions.delete')}
          useSaveDropdown={true}
          saveDropdownStorageKey="announcements:saveActionPreference"
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('announcements.messages.deleteTitle')}
        message={t('announcements.messages.confirmDelete', { name: formData.name || '' })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('announcements.quickList.title', 'Announcements')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigateRef.current(`/pbx/incoming-tools/announcements/${item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigateRef.current(`/pbx/incoming-tools/announcements/${item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('announcements.quickList.searchPlaceholder', 'Search by name or description')}
        emptyMessage={t('announcements.quickList.empty', 'No announcements found')}
        headerIcon={<Volume2 className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

